import { useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// RC52-SHP-001: Safari-safe date picker — selecting a day populates the field and closes the calendar; reopen to edit.
// Values stay plain YYYY-MM-DD strings (no timezone conversion), exactly like the native date input they replace.
export const DatePickerField = ({ value, onChange, min, max, disabled = false, required = false, clearable = !required,
  placeholder = "Pick a date", testId = "date-picker", className = "h-11" }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const minDate = min === "today" ? new Date(new Date().setHours(0, 0, 0, 0)) : min ? parseISO(min) : undefined;
  const maxDate = max ? parseISO(max) : undefined;
  const rules = [minDate && { before: minDate }, maxDate && { after: maxDate }].filter(Boolean);
  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" disabled={disabled} data-testid={testId}
            className={`${className} w-full justify-start border-input bg-white font-normal text-charcoal hover:bg-sand ${clearable && value ? "pr-8" : ""}`}>
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-warmgrey" />
            {selected ? format(selected, "MMM d, yyyy") : <span className="truncate text-warmgrey">{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-popover`}>
          <Calendar mode="single" selected={selected} disabled={rules.length ? rules : undefined} defaultMonth={selected || minDate}
            onSelect={(d) => { onChange(d ? toISO(d) : ""); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
      {clearable && value && !disabled && (
        <button type="button" onClick={() => onChange("")} aria-label="Clear date" data-testid={`${testId}-clear`}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-warmgrey hover:text-charcoal">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {required && (
        <input tabIndex={-1} aria-hidden="true" required value={value || ""} onChange={() => {}}
          className="pointer-events-none absolute bottom-0 left-4 h-px w-px opacity-0" />
      )}
    </div>
  );
};
